import { lazy, Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Header } from './navigation/Header';
import { Sidebar } from './navigation/Sidebar';
import { Dashboard } from './dashboard/Dashboard';

// Redirect component for prep routes
function PrepRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/simulation/${id}`} replace />;
}

function RouteScrollToTop() {
  const location = useLocation();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
}
import { SimulationDetail } from '@/pages/SimulationDetail';
import AnamSimulationPage from '@/pages/AnamSimulationPage';
import { MySimulations } from '@/pages/MySimulations';
import { MyProgress } from '@/pages/MyProgress';
import { Settings } from '@/pages/Settings';
import { AdminDashboard } from '@/pages/AdminDashboard';
import { AgentManagement } from '@/pages/AgentManagement';
const CreateAgent = lazy(() => import('@/pages/CreateAgent').then(m => ({ default: m.CreateAgent })));
const EditAgent = lazy(() => import('@/pages/EditAgent').then(m => ({ default: m.EditAgent })));
import AgentProfileView from '@/pages/AgentProfileView';
import UserManagement from '@/pages/UserManagement';
import EmailHistory from '@/pages/EmailHistory';
import CreateUser from '@/pages/CreateUser';
import EditUser from '@/pages/EditUser';
import CompanyManagement from '@/pages/CompanyManagement';
import CreateCompany from '@/pages/CreateCompany';
import EditCompany from '@/pages/EditCompany';
import CompanyDetail from '@/pages/CompanyDetail';
import PhysicalAssessments from '@/pages/PhysicalAssessments';
import CreateAssessment from '@/pages/CreateAssessment';
import EditAssessment from '@/pages/EditAssessment';
import Leaderboards from '@/pages/Leaderboards';
const SimulationResults = lazy(() => import('@/pages/SimulationResults'));
import MySessions from '@/pages/MySessions';
import ReleaseNotes from '@/pages/ReleaseNotes';
import { CompanyDashboard } from '@/pages/CompanyDashboard';
import { GamificationDashboard } from '@/pages/GamificationDashboard';
import CoachingSessionPage from '@/pages/CoachingSessionPage';
const CoachingResultsPage = lazy(() => import('@/pages/CoachingResultsPage'));
import CoachingHistoryPage from '@/pages/CoachingHistoryPage';
import DebriefSessionPage from '@/pages/DebriefSessionPage';
import TemplateCoachingSessionPage from '@/pages/TemplateCoachingSessionPage';
import LearningPathways from '@/pages/LearningPathways';
import PathwayDetailPage from '@/pages/PathwayDetailPage';
import QuizModulePage from '@/pages/QuizModulePage';
import ReadingModulePage from '@/pages/ReadingModulePage';
const PathwayManagement = lazy(() => import('@/pages/PathwayManagement'));
import CertificateManagement from '@/pages/CertificateManagement';
const KnowledgeBase = lazy(() => import('@/pages/KnowledgeBase'));
const ContentStudioPage = lazy(() => import('@/pages/ContentStudioPage'));
const RoundtablePage = lazy(() => import('@/pages/RoundtablePage'));
const AgentTemplates = lazy(() => import('@/pages/AgentTemplates'));
import CoachingTemplates from '@/pages/CoachingTemplates';
import CreateCoachingTemplate from '@/pages/CreateCoachingTemplate';
import EditCoachingTemplate from '@/pages/EditCoachingTemplate';
import RubricTemplates from '@/pages/RubricTemplates';
import CreateRubricTemplate from '@/pages/CreateRubricTemplate';
import EditRubricTemplate from '@/pages/EditRubricTemplate';
import UserViewDebugger from '@/pages/UserViewDebugger';
import SystemConfiguration from '@/pages/SystemConfiguration';
const TenantManagement = lazy(() => import('@/pages/TenantManagement'));
const TenantDetail = lazy(() => import('@/pages/TenantDetail'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const BugReports = lazy(() => import('@/pages/BugReports'));
const UserActivityReport = lazy(() => import('@/pages/UserActivityReport'));
const OrganisationManagement = lazy(() => import('@/pages/OrganisationManagement'));
const NetworkQualification = lazy(() => import('@/pages/NetworkQualification'));
import { ProtectedRoute } from './ProtectedRoute';
import { OrganisationAccessRoute } from './OrganisationAccessRoute';
import { ProfileCompletionBanner } from './ProfileCompletionBanner';
import { OnboardingProvider, OnboardingModal, SuperAdminOnboardingWizard } from './onboarding';
import { BugReportWidget } from './support/BugReportWidget';
import { ErrorReportAcknowledgement } from './support/ErrorReportAcknowledgement';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useCpdEnabled } from '@/contexts/TenantContext';

export function MainApp() {
  const { hasEnterpriseHierarchy } = useOrganisation();
  const cpdEnabled = useCpdEnabled();
  const location = useLocation();
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);

  useEffect(() => {
    setMobileNavigationOpen(false);
  }, [location.pathname]);

  return (
    <OnboardingProvider>
      <RouteScrollToTop />
      <ErrorReportAcknowledgement />
      <div className="min-h-screen flex w-full bg-background">
        <div className="hidden shrink-0 lg:block">
          <Sidebar />
        </div>
        <Sheet open={mobileNavigationOpen} onOpenChange={setMobileNavigationOpen}>
          <SheetContent
            side="left"
            className="w-[18rem] max-w-[90vw] border-0 bg-primary p-0 [&>button]:text-white"
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Sidebar allowCollapse={false} className="w-full" />
          </SheetContent>
        </Sheet>
          <div className="flex min-w-0 flex-1 flex-col">
            <ProfileCompletionBanner />
            <Header onOpenNavigation={() => setMobileNavigationOpen(true)} />
          <main className="flex-1">
            <Suspense fallback={<div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
            <Routes>
              {/* Public route for password reset */}
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={<Dashboard />} />
              <Route path="/simulation/:id/prep" element={<PrepRedirect />} />
              <Route path="/simulation/:id" element={<SimulationDetail />} />
              <Route path="/simulation/:id/video" element={<AnamSimulationPage />} />
              <Route path="/simulation/results/:id" element={<SimulationResults />} />
              <Route path="/coaching/:id/session" element={<CoachingSessionPage />} />
              <Route path="/coaching/template/:templateId/session" element={<TemplateCoachingSessionPage />} />
              <Route path="/coaching/results/:sessionId" element={<CoachingResultsPage />} />
              <Route path="/coaching/history" element={<CoachingHistoryPage />} />
              <Route path="/coaching/debrief" element={<DebriefSessionPage />} />
              <Route path="/learning/pathways" element={<LearningPathways />} />
              <Route path="/learning/pathway/:pathwayId" element={<PathwayDetailPage />} />
              <Route path="/learning/quiz/:moduleId" element={<QuizModulePage />} />
              <Route path="/learning/reading/:moduleId" element={<ReadingModulePage />} />
              <Route path="/help/knowledge-base" element={<KnowledgeBase />} />
              <Route path="/connection-check" element={<NetworkQualification />} />
              <Route path="/content-studio" element={
                <ProtectedRoute allowedRoles={['admin', 'company_admin']} allowEnterpriseAssetManagers>
                  <ContentStudioPage />
                </ProtectedRoute>
              } />
              <Route path="/roundtable" element={
                <ProtectedRoute allowedRoles={['admin', 'company_admin', 'user']} allowEnterpriseAssetManagers>
                  <RoundtablePage />
                </ProtectedRoute>
              } />
              <Route path="/admin/studio" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ContentStudioPage />
                </ProtectedRoute>
              } />
              <Route path="/simulations" element={<MySimulations />} />
              <Route path="/my-sessions" element={<MySessions />} />
              <Route path="/whats-new" element={<ReleaseNotes />} />
              <Route path="/progress" element={<MyProgress />} />
              <Route path="/leaderboards" element={<Leaderboards />} />
              <Route path="/settings" element={<Settings />} />
              
              {/* Admin Routes */}
              <Route 
                path="/admin" 
                element={
                  hasEnterpriseHierarchy ? (
                    <OrganisationAccessRoute>
                      <AdminDashboard />
                    </OrganisationAccessRoute>
                  ) : (
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  )
                } 
              />
              <Route 
                path="/admin/users" 
                element={
                  <OrganisationAccessRoute>
                    <UserManagement />
                  </OrganisationAccessRoute>
                } 
              />
              <Route 
                path="/admin/users/create" 
                element={
                  <OrganisationAccessRoute>
                    <CreateUser />
                  </OrganisationAccessRoute>
                } 
              />
              <Route
                path="/admin/users/:id/edit"
                element={
                  <OrganisationAccessRoute>
                    <EditUser />
                  </OrganisationAccessRoute>
                }
              />
              <Route
                path="/admin/email-history"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EmailHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/activity"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <UserActivityReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/organisation"
                element={
                  <OrganisationAccessRoute>
                    <OrganisationManagement />
                  </OrganisationAccessRoute>
                }
              />
              <Route
                path="/admin/agent-templates"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'company_admin']} allowEnterpriseAssetManagers>
                    <AgentTemplates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/agents"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'company_admin']} allowEnterpriseAssetManagers>
                    <AgentManagement />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/admin/agents/create" 
                element={
                  <ProtectedRoute allowedRoles={['admin', 'company_admin']} allowEnterpriseAssetManagers>
                    <CreateAgent />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/agents/:id/edit" 
                element={
                  <ProtectedRoute allowedRoles={['admin', 'company_admin']} allowEnterpriseAssetManagers>
                    <EditAgent />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/admin/agents/:id/view"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'company_admin']} allowEnterpriseAssetManagers>
                    <AgentProfileView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pathways"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'company_admin']} allowEnterpriseAssetManagers>
                    <PathwayManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/certificates"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'company_admin']} allowEnterpriseAssetManagers>
                    <CertificateManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/assessments"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <PhysicalAssessments />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/assessments/create" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CreateAssessment />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/assessments/:id/edit" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EditAssessment />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/companies" 
                element={
                  hasEnterpriseHierarchy
                    ? <Navigate to="/admin/organisation" replace />
                    : (
                      <ProtectedRoute allowedRoles={['admin']}>
                        <CompanyManagement />
                      </ProtectedRoute>
                    )
                } 
              />
              <Route 
                path="/admin/companies/create" 
                element={
                  hasEnterpriseHierarchy
                    ? <Navigate to="/admin" replace />
                    : (
                      <ProtectedRoute allowedRoles={['admin']}>
                        <CreateCompany />
                      </ProtectedRoute>
                    )
                } 
              />
              <Route 
                path="/admin/companies/:id/edit" 
                element={
                  hasEnterpriseHierarchy
                    ? <Navigate to="/admin/organisation" replace />
                    : (
                      <ProtectedRoute allowedRoles={['admin']}>
                        <EditCompany />
                      </ProtectedRoute>
                    )
                } 
              />
              <Route 
                path="/admin/companies/:id" 
                element={
                  hasEnterpriseHierarchy
                    ? <Navigate to="/admin/organisation" replace />
                    : (
                      <ProtectedRoute allowedRoles={['admin']}>
                        <CompanyDetail />
                      </ProtectedRoute>
                    )
                } 
              />
              
              {/* Coaching Templates */}
              <Route 
                path="/admin/coaching-templates" 
                element={
                  <ProtectedRoute allowedRoles={['admin']} allowEnterpriseAssetManagers>
                    <CoachingTemplates />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/coaching-templates/create" 
                element={
                  <ProtectedRoute allowedRoles={['admin']} allowEnterpriseAssetManagers>
                    <CreateCoachingTemplate />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/coaching-templates/edit/:id" 
                element={
                  <ProtectedRoute allowedRoles={['admin']} allowEnterpriseAssetManagers>
                    <EditCoachingTemplate />
                  </ProtectedRoute>
                } 
              />
              
              {/* Rubric Templates */}
              <Route 
                path="/admin/rubric-templates" 
                element={
                  <ProtectedRoute allowedRoles={['admin']} allowEnterpriseAssetManagers>
                    <RubricTemplates />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/rubric-templates/create" 
                element={
                  <ProtectedRoute allowedRoles={['admin']} allowEnterpriseAssetManagers>
                    <CreateRubricTemplate />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/rubric-templates/edit/:id" 
                element={
                  <ProtectedRoute allowedRoles={['admin']} allowEnterpriseAssetManagers>
                    <EditRubricTemplate />
                  </ProtectedRoute>
                } 
              />
              
              {/* User View Debugger */}
              <Route
                path="/admin/user-view-debugger"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <UserViewDebugger />
                  </ProtectedRoute>
                }
              />

              {/* System Configuration - Super Admin Only */}
              <Route
                path="/admin/system-config"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <SystemConfiguration />
                  </ProtectedRoute>
                }
              />

              {/* Tenant Management - Super Admin Only */}
              <Route
                path="/admin/tenants"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <TenantManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tenants/:id"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <TenantDetail />
                  </ProtectedRoute>
                }
              />

              {/* Company Admin Routes */}
              <Route 
                path="/company-dashboard" 
                element={
                  hasEnterpriseHierarchy
                    ? <Navigate to="/admin/organisation" replace />
                    : (
                      <ProtectedRoute allowedRoles={['admin', 'company_admin']}>
                        <CompanyDashboard />
                      </ProtectedRoute>
                    )
                } 
              />
              <Route 
                path="/company-dashboard/gamification" 
                element={
                  cpdEnabled ? (
                    <ProtectedRoute allowedRoles={['admin', 'company_admin']}>
                      <GamificationDashboard />
                    </ProtectedRoute>
                  ) : (
                    <Navigate to="/" replace />
                  )
                } 
              />
              <Route path="/team-performance" element={<div className="p-8">Team Performance (Coming Soon)</div>} />
              <Route
                path="/bug-reports"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <BugReports />
                  </ProtectedRoute>
                }
              />
            </Routes>
            </Suspense>
          <OnboardingModal />
          <SuperAdminOnboardingWizard />
          <BugReportWidget />
        </main>
      </div>
    </div>
    </OnboardingProvider>
  );
}
